package io.horion.auth.api.repository

import io.horion.auth.api.models.domain.*
import io.horion.auth.api.models.response.Response
import io.horion.auth.api.services.ApiResponseDto
import io.horion.auth.api.services.AuthService
import io.horion.auth.api.services.CheckSessionResponseDto
import io.horion.auth.domain.models.SsoCodeResponse
import kotlin.coroutines.cancellation.CancellationException

class AuthRepositoryImpl(
    private val authService: AuthService
): AuthRepository {
    private fun normalizePhone(phone: String): String? {
        val digits = phone.filter { it.isDigit() }

        return when (digits.length) {
            9 -> digits
            12 if digits.startsWith("237") -> digits.takeLast(9)
            14 if digits.startsWith("00237") -> digits.takeLast(9)
            else -> null
        }
    }

    override suspend fun register(email: String, phone: String, password: String): Response {
        return try {
            if (email.isBlank() && phone.isBlank()) {
                return Response(false, "Email ou téléphone requis")
            }

            val formattedPhone = phone.takeIf { it.isNotBlank() }?.let { normalizePhone(it) }
            if (phone.isNotBlank() && formattedPhone == null) {
                return Response(
                    false,
                    "Format de téléphone invalide. Utilisez 9 chiffres (ex: 612345678) ou avec indicatif (+237612345678)"
                )
            }

            val request = RegisterRequest(
                email = email.takeIf { it.isNotBlank() },
                phone = formattedPhone,
                password = password
            )

            authService.register(request)
        } catch (e: Exception) {
            Response(false, "Erreur lors de l'inscription , essayer plus tard")
        }
    }

    override suspend fun login(email:String?, phone:String?, password:String,isMobileClient: Boolean): Response {
        return try {
            val request = when {
                email != null -> LoginWithEmail(email = email, password = password)
                phone != null -> LoginWithPhone(phone = phone, password = password)
                else -> return Response(false, "Identifiants de connexion manquants")
            }

            authService.login(request, isMobileClient)
        } catch (e: Exception) {
            Response(false, "Erreur de connexion : Serveur injoignable")
        }
    }

    override suspend fun verify(email: String, phone: String, model: LoginMethod): Response {
        return try {
            val request = if (model == LoginMethod.EMAIL) {
                VerifyEmail(email = email)
            } else {
                VerifyPhone(phone = phone)
            }
            authService.verify(request, model)
        } catch (e: Exception) {
            Response(false, "Serveur injoignable, reesayer plus tard")
        }
    }

    override suspend fun confirm(email: String, phone: String, code: String, model: LoginMethod): Response {
        return try {
            val request = if (model == LoginMethod.EMAIL) {
                ConfirmEmail(email = email, code = code)
            } else {
                ConfirmPhone(phone = phone, code = code)
            }
            authService.confirm(request, model)
        } catch (e : Exception) {
            Response(false,"Serveur injoignable, reesayer plus tard")
        }
    }

    override suspend fun forgotPassword(email: String, phone: String): Response {
        return try {
            val method = if (email.isNotBlank()) {
                LoginMethod.EMAIL
            } else {
                LoginMethod.PHONE
            }

            val identity = email.ifBlank {
                phone
            }

            val success = authService.initiatePasswordReset(identity,method)
            if (success.success) {
                Response(true,"Code envoyer avec success")
            } else {
                Response(false,success.message)
            }
        } catch (e: Exception) {
            Response(false,"Erreur: ${e.message}")
        }
    }

    override suspend fun resetPassword(email:String, phone:String, code: String, newPassword: String): Response {
        return try {
            val method = if (email.isNotBlank()) {
                LoginMethod.EMAIL
            } else {
                LoginMethod.PHONE
            }

            val identity = email.ifBlank {
                phone
            }

            if (code.length != 6) {
                return Response(false,"Le code doit contenir exactement 6 chiffres")
            }

            if (newPassword.length < 8) {
                return Response(false,"Le mot de passe doit contenir au moins 8 caractères")
            }
            val request = ResetPasswordRequest(
                identity = identity,
                code = code.toInt(),
                newPassword = newPassword
            )
            val response = authService.resetPassword(request,method)
            if (response){
                Response(true,"reset reussi")
            } else {
                Response(false,"reset echoue")
            }

        } catch (e: Exception) {
            Response(false,"Serveur injoignable, reesayer plus tard")
        }
    }

    override suspend fun logout(): Response? {
        return try {
            val result = authService.logout()
            try {

                if (result == true){
                    Response(true,"logout reussi")
                } else {
                    Response(false,"Erreur ")
                }

            } catch (e: Exception) {
                Response(false,e.message.toString())
            }
        } catch (e: Exception) {
            Response(false,"Erreur de déconnexion: ${e.message}")
        }
    }

    override suspend fun logoutLocal(service : String,userUuid: String): Response? {
        return try {
            val result = authService.logoutLocal(service,userUuid)
            try {
                if (result){
                    Response(true,"logout reussi")
                } else {
                    Response(false,"Erreur ")
                }

            } catch (e: Exception) {
                Response(false,e.message.toString())
            }
        } catch (e: Exception) {
            Response(false,"Erreur de déconnexion: ${e.message}")
        }
    }

    override suspend fun checkSession(serviceName: String?): CheckSessionResponseDto {
        return try {
            authService.checkSession(serviceName)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            CheckSessionResponseDto(connected = false, accounts = emptyList())
        }
    }

    override suspend fun switchAccount(userUuid: String): ApiResponseDto {
        return try {
            authService.switchAccount(userUuid)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            ApiResponseDto(success = false, message = "Erreur réseau : ${e.message}")
        }
    }

    override suspend fun send2FaOtp(channelValue: String, method: String): Response {
        return try {
            val result = authService.send2FaOtp(channelValue, method)
            return result
        } catch (e: Exception) {
            Response(false,"Erreur de déconnexion: ${e.message}")
        }
    }

    override suspend fun verify2FaAndEnable(channelValue: String, method: String, code: String): Response {
        return try {
            val result = authService.verify2FaAndEnable(channelValue, method, code)
            return result
        } catch (e: Exception) {
            Response(false,"Erreur de déconnexion: ${e.message}")
        }
    }

    override suspend fun disable2FA(): Response {
        return try {
            val result = authService.disable2FA()
            return result
        } catch (e: Exception) {
            Response(false,"Erreur de déconnexion: ${e.message}")
        }
    }

    override suspend fun generateSsoCodeForMobile(): SsoCodeResponse? {
        return try {
            authService.generateCodeForMobile()
        } catch (t: Throwable) {
            null
        }
    }


}